/**
 * «Когда выложить в ‹Сеть›» (задачи Оли №385, №388): сетка месяца как у календаря браузера, но у дней с
 * уже запланированными постами этой сети — точка и число (черновики тоже), сегодня обведено, выбранный
 * день закрашен; поле времени; под сеткой — посты выбранного дня (время, миниатюра, номер, ссылка в новой
 * вкладке; эта публикация подсвечена). Сохраняет вызывающий (`onSave`) — та же дорожка `STALE`, что у
 * переходов. Бесплатно.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Dialog } from "@/components/trends/Dialog";
import { btn, btnGhost, btnPrimary, ErrorLine, input } from "@/components/trends/ui";
import { thumbUrl } from "@/lib/media";
import { byDay, dayKey, monthGrid, monthTitle, shiftMonth, splitWhen, type Month } from "@/lib/postSchedule";
import { loadCalendar, postsKeys } from "@/lib/postsApi";
import { dayLabel } from "@/lib/postsTabs";

const WEEK = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

export function ScheduleDialog({
  open,
  planId,
  network,
  networkName,
  value,
  onClose,
  onSave,
}: {
  open: boolean;
  planId: number;
  network: string;
  networkName: string;
  value: string | null;
  onClose: () => void;
  onSave: (date: string | null) => void;
}) {
  const start = splitWhen(value);
  const [day, setDay] = useState(start.day);
  const [time, setTime] = useState(start.time);
  const [month, setMonth] = useState<Month>({ year: Number(start.day.slice(0, 4)), month: Number(start.day.slice(5, 7)) });
  const q = useQuery({ queryKey: postsKeys.calendar(network), queryFn: () => loadCalendar(network), enabled: open });
  const posts = byDay(q.data ?? []);
  const today = dayKey(new Date());
  const ofDay = posts.get(day) ?? [];

  return (
    <Dialog open={open} title={`Когда выложить в ${networkName}`} onClose={onClose}>
      <div className="grid gap-3 p-4" data-annot="календарь выкладки">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-medium text-fg">{monthTitle(month)}</span>
          <button
            type="button"
            className={`${btnGhost} ml-auto`}
            aria-label="Предыдущий месяц"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
          >
            <ChevronLeft size={14} />
          </button>
          <button type="button" className={btnGhost} aria-label="Следующий месяц" onClick={() => setMonth((m) => shiftMonth(m, 1))}>
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[12.5px]">
          {WEEK.map((w) => (
            <span key={w} className="py-1 text-[11px] text-fg-faint">
              {w}
            </span>
          ))}
          {monthGrid(month.year, month.month)
            .flat()
            .map((d) => {
              const n = posts.get(d.day)?.length ?? 0;
              const chosen = d.day === day;
              return (
                <button
                  key={d.day}
                  type="button"
                  onClick={() => {
                    setDay(d.day);
                    // день соседнего месяца — сетка переходит на его месяц
                    if (!d.inMonth) setMonth({ year: Number(d.day.slice(0, 4)), month: Number(d.day.slice(5, 7)) });
                  }}
                  title={n ? `запланировано: ${n}` : "свободно"}
                  className={
                    "relative grid h-10 place-items-center rounded-[var(--radius-ui)] border " +
                    (chosen
                      ? "border-accent bg-accent text-bg"
                      : d.day === today
                        ? "border-accent text-fg"
                        : "border-transparent hover:bg-bg-subtle " + (d.inMonth ? "text-fg" : "text-fg-faint"))
                  }
                >
                  {d.date}
                  {n ? (
                    <span
                      className={
                        "absolute bottom-0.5 left-1/2 flex -translate-x-1/2 items-center gap-0.5 text-[9.5px] leading-none " +
                        (chosen ? "text-bg" : "text-accent-text")
                      }
                    >
                      <span className={`size-1.5 rounded-full ${chosen ? "bg-bg" : "bg-accent"}`} />
                      {n > 1 ? n : null}
                    </span>
                  ) : null}
                </button>
              );
            })}
        </div>
        {q.isError ? <ErrorLine error={q.error} /> : null}

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (day && time) onSave(`${day}T${time}`);
          }}
        >
          <span className="text-[12.5px] text-fg-muted">{dayLabel(day)} в</span>
          <input
            type="time"
            aria-label="Время выкладки"
            className={`${input} w-28`}
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
          <button type="submit" className={btnPrimary} disabled={!time}>
            Сохранить
          </button>
          {value ? (
            <button type="button" className={btn} onClick={() => onSave(null)}>
              Убрать дату
            </button>
          ) : null}
          <button type="button" className={btnGhost} onClick={onClose}>
            Отмена
          </button>
        </form>

        <div className="grid gap-1.5 border-t border-border pt-2" data-annot="посты дня">
          <span className="text-[12px] text-fg-faint">
            {ofDay.length ? `На ${dayLabel(day)} в ${networkName} уже запланировано:` : `На ${dayLabel(day)} в ${networkName} ничего не запланировано.`}
          </span>
          {ofDay.length ? (
            <div className="flex flex-wrap gap-1.5">
              {ofDay.map((it) => {
                const thumb = it.video_ms !== null ? it.poster_path : it.asset_path;
                const own = it.plan_id === planId;
                return (
                  <Link
                    key={it.plan_id}
                    to={`/posts/${it.plan_id}?net=${network}`}
                    target="_blank"
                    title={own ? "эта публикация" : `публикация №${it.plan_id} — откроется в новой вкладке`}
                    className={
                      "flex items-center gap-1.5 rounded-[var(--radius-ui)] border px-1.5 py-1 text-[12px] " +
                      (own ? "border-accent text-accent-text" : "border-border text-fg-muted hover:text-fg")
                    }
                  >
                    {thumb ? <img src={thumbUrl(thumb)} alt="" className="size-8 rounded-[3px] object-cover" /> : null}
                    {it.publish_date.slice(11, 16)} · №{it.plan_id}
                    {it.status === "draft" ? <span className="text-fg-faint">черновик</span> : null}
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
